import React, { useMemo, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import i18n from 'i18n';
import { get, map } from 'lodash';
import getLocalized from 'utils/get-localized';
import { showOverlay } from 'actions/overlay';
import Button from 'components/Button';
import {
  CastList,
  CastTitle,
  CastWrapper,
  DescriptionCastWrapper,
  DescriptionWrapper,
  ExpandTextWrapper,
} from './StyledComponents';

const SHOW_MORE_CONFIG = {
  leaveFor: {
    right: '@COVER',
  },
};

const EXPAND_BUTTON_TEXT_SIZE = 150;
const CUT_REG_EXP = /(\s[А-Яа-яA-Za-z]|[.\s,]){1,2}$/;

const DetailsDescription = ({ data, maxTextLength, isVisible, onFocus }) => {
  const dispatch = useDispatch();
  const defaultDescriptionFallback = get(data.title, ['summaryLong']) || get(data.title, ['summaryShort']) || '';
  let description = getLocalized(data.titles, defaultDescriptionFallback, 'summaryShort');

  description = description.replace(/<a[^>]*>(.*?)<\/a>/, '$1');

  const normalizedDescription = useMemo(() => {
    if (description.length <= maxTextLength) return description;
    return `${description
      .slice(0, maxTextLength - EXPAND_BUTTON_TEXT_SIZE - 3)
      // replace last 2-3 symbols and replace them with 3 dot
      .replace(CUT_REG_EXP, '')}...`;
  }, [description, maxTextLength]);

  const isExpandTextButtonVisible = normalizedDescription !== description;

  const actorsInfo = useMemo(
    () =>
      map(data.actors, (item) => getLocalized(item, item.UA))
        .slice(0, 5)
        .join(', '),
    [data.actors],
  );
  const directorsInfo = useMemo(
    () =>
      map(data.directors, (item) => getLocalized(item, item.UA))
        .slice(0, 5)
        .join(', '),
    [data.directors],
  );

  const onShowMore = useCallback(() => {
    dispatch(
      showOverlay({
        type: 'text',
        returnFocusTo: '@EXPAND_DESCRIPTION_BUTTON',
        options: { fullDescription: description },
      }),
    );
  }, [description, dispatch]);

  return (
    <DescriptionCastWrapper useFixedHeight={!isVisible}>
      <DescriptionWrapper dangerouslySetInnerHTML={{ __html: normalizedDescription }} />
      {isExpandTextButtonVisible && (
        <ExpandTextWrapper sectionName={'EXPAND_DESCRIPTION_BUTTON'} config={SHOW_MORE_CONFIG}>
          <Button onFocus={onFocus} onEnter={onShowMore}>
            {i18n.t('detailsVOD.moreButton')}
          </Button>
        </ExpandTextWrapper>
      )}
      {actorsInfo && (
        <CastWrapper>
          <CastTitle>{i18n.t('detailsVOD.actors')}</CastTitle>
          <CastList>{actorsInfo}</CastList>
        </CastWrapper>
      )}
      {directorsInfo && (
        <CastWrapper>
          <CastTitle>{i18n.t('detailsVOD.directors')}</CastTitle>
          <CastList>{directorsInfo}</CastList>
        </CastWrapper>
      )}
    </DescriptionCastWrapper>
  );
};

DetailsDescription.defaultProps = {
  maxTextLength: 350,
};

DetailsDescription.propTypes = {
  data: PropTypes.object.isRequired,
  maxTextLength: PropTypes.number.isRequired,
  onFocus: PropTypes.func.isRequired,
  isVisible: PropTypes.bool,
};

export default memo(DetailsDescription);
